import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FilterRuleRequest, VALUELESS_OPERATORS } from '../../../models/segment.model';
import { ContactState, ContactCycle, ContactOrigin, ContactAttributeDefinition } from '../../../../contactos/models/contacto.model';
import { CompanySector, CompanySize, CompanyRelationshipType, CompanyCycleResponse, CompanyStateLeadResponse } from '../../../../empresas/models/empresa.model';

interface FlatConditionUI {
  property: string;
  operator: string;
  valueExpression: string;
  groupOperator: 'AND' | 'OR';
}

@Component({
  selector: 'app-segment-filter-builder',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './segment-filter-builder.component.html',
})
export class SegmentFilterBuilderComponent implements OnInit {

  @Input() rules!: FormArray;
  @Input() segmentType: 'STATIC' | 'DYNAMIC' = 'DYNAMIC';
  @Input() autoAddCondition = true;

  @Input() contactStates: ContactState[] = [];
  @Input() contactCycles: ContactCycle[] = [];
  @Input() contactOrigins: ContactOrigin[] = [];
  @Input() customAttributes: ContactAttributeDefinition[] = [];

  @Input() companySectors: CompanySector[] = [];
  @Input() companySizes: CompanySize[] = [];
  @Input() companyRelationshipTypes: CompanyRelationshipType[] = [];
  @Input() companyCycles: CompanyCycleResponse[] = [];
  @Input() companyStateLeads: CompanyStateLeadResponse[] = [];

  conditions: FlatConditionUI[] = [];

  private operatorsByCategory: Record<string, { value: string; label: string }[]> = {
    text: [
      { value: 'EQUALS', label: 'Es igual a' },
      { value: 'NOT_EQUALS', label: 'No es igual a' },
      { value: 'CONTAINS', label: 'Contiene' },
      { value: 'NOT_CONTAINS', label: 'No contiene' },
      { value: 'STARTS_WITH', label: 'Empieza con' },
      { value: 'ENDS_WITH', label: 'Termina con' }
    ],
    select: [
      { value: 'EQUALS', label: 'Es igual a' },
      { value: 'NOT_EQUALS', label: 'No es igual a' }
    ],
    number: [
      { value: 'EQUALS', label: 'Es igual a' },
      { value: 'NOT_EQUALS', label: 'No es igual a' },
      { value: 'GREATER_THAN', label: 'Es mayor que' },
      { value: 'LESS_THAN', label: 'Es menor que' },
      { value: 'GREATER_THAN_OR_EQUALS', label: 'Mayor o igual que' },
      { value: 'LESS_THAN_OR_EQUALS', label: 'Menor o igual que' }
    ],
    date: [
      { value: 'GREATER_THAN_OR_EQUALS', label: 'En o después de' },
      { value: 'LESS_THAN_OR_EQUALS', label: 'En o antes de' },
      { value: 'BETWEEN', label: 'Entre' },
      { value: 'LAST_N_DAYS', label: 'En los últimos N días' },
      { value: 'NEXT_N_DAYS', label: 'En los próximos N días' },
      { value: 'LAST_N_MONTHS', label: 'En los últimos N meses' },
      { value: 'THIS_QUARTER', label: 'Este trimestre' },
      { value: 'IS_NULL', label: 'Está vacío' },
      { value: 'IS_NOT_NULL', label: 'No está vacío' }
    ]
  };

  contactStaticProps = [
    { id: 'idContactState', name: 'Estado del Contacto', inputType: 'select_state' },
    { id: 'idContactCycle', name: 'Ciclo de Contacto', inputType: 'select_cycle' },
    { id: 'idOrigin', name: 'Origen del Contacto', inputType: 'select_origin' }
  ];

  contactTextProps = [
    { id: 'fullname', name: 'Nombre', inputType: 'text' },
    { id: 'lastname', name: 'Apellido', inputType: 'text' },
    { id: 'mail', name: 'Correo electrónico', inputType: 'text' },
    { id: 'phone', name: 'Teléfono', inputType: 'text' },
    { id: 'position', name: 'Cargo', inputType: 'text' }
  ];

  contactDateProps = [
    { id: 'datetimeCreate', name: 'Fecha de creación', inputType: 'date' },
    { id: 'datetimeUpdate', name: 'Fecha de actualización', inputType: 'date' }
  ];

  companyStaticProps = [
    { id: 'idCompanyRelationshipType', name: 'Tipo de Relación', inputType: 'select_comp_relation' },
    { id: 'idCompanySize', name: 'Tamaño de Empresa', inputType: 'select_comp_size' },
    { id: 'idCompanySector', name: 'Sector / Industria', inputType: 'select_comp_sector' },
    { id: 'idCompanyCycle', name: 'Ciclo de Empresa', inputType: 'select_comp_cycle' },
    { id: 'idCompanyStateLead', name: 'Estado de Empresa', inputType: 'select_comp_state_lead' }
  ];

  companyDateProps = [
    { id: 'companyDatetimeCreated', name: 'Fecha de creación (empresa)', inputType: 'date' },
    { id: 'companyDatetimeUpdated', name: 'Fecha de actualización (empresa)', inputType: 'date' }
  ];

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    if (this.rules.length > 0) {
      this.rebuildUIFromFormArray();
    } else if (this.autoAddCondition) {
      this.addCondition();
    }
  }

  addCondition(): void {
    this.conditions.push({ property: '', operator: 'EQUALS', valueExpression: '', groupOperator: 'AND' });
    this.syncToFormArray();
  }

  removeCondition(index: number): void {
    this.conditions.splice(index, 1);
    this.syncToFormArray();
  }

  onInterOperatorChange(index: number, value: string): void {
    this.conditions[index].groupOperator = value as 'AND' | 'OR';
    this.syncToFormArray();
  }

  onConditionPropertyChange(condition: FlatConditionUI): void {
    condition.valueExpression = '';
    const ops = this.categoryOperators(condition.property);
    if (!ops.some(o => o.value === condition.operator)) {
      condition.operator = ops[0]?.value ?? 'EQUALS';
    }
    this.syncToFormArray();
  }

  onConditionChange(): void {
    this.syncToFormArray();
  }

  onConditionOperatorChange(condition: FlatConditionUI): void {
    if (this.getPropertyInputType(condition.property) === 'date' || VALUELESS_OPERATORS.has(condition.operator)) {
      condition.valueExpression = '';
    }
    this.syncToFormArray();
  }

  operatorsFor(condition: FlatConditionUI): { value: string; label: string }[] {
    let list = this.categoryOperators(condition.property);
    if (condition.operator && !list.some(o => o.value === condition.operator)) {
      list = [{ value: condition.operator, label: this.operatorLabel(condition.operator) }, ...list];
    }
    return list;
  }

  operatorNeedsValue(operator: string): boolean {
    return !VALUELESS_OPERATORS.has(operator);
  }

  getValueControl(condition: FlatConditionUI): string {
    if (VALUELESS_OPERATORS.has(condition.operator)) return 'none';
    const it = this.getPropertyInputType(condition.property);
    if (it === 'date') {
      if (condition.operator === 'LAST_N_DAYS' || condition.operator === 'NEXT_N_DAYS' || condition.operator === 'LAST_N_MONTHS') return 'relative_days';
      if (condition.operator === 'BETWEEN') return 'between_date';
      return 'date';
    }
    return it;
  }

  betweenPart(condition: FlatConditionUI, index: 0 | 1): string {
    return (condition.valueExpression || '').split('|')[index] || '';
  }

  setBetweenPart(condition: FlatConditionUI, index: 0 | 1, value: string): void {
    const parts = (condition.valueExpression || '').split('|');
    while (parts.length < 2) parts.push('');
    parts[index] = value || '';
    condition.valueExpression = `${parts[0] || ''}|${parts[1] || ''}`;
    this.onConditionChange();
  }

  getCustomAttributeListOptions(propertyId: string): string[] {
    const attr = this.customAttributes.find(a => String(a.id) === String(propertyId));
    if (!attr?.listOptions) return [];
    try { return JSON.parse(attr.listOptions); } catch { return []; }
  }

  getPropertyInputType(propertyId: string): string {
    if (!propertyId) return 'empty';
    let found = this.contactStaticProps.find(p => p.id === propertyId)
             ?? this.contactTextProps.find(p => p.id === propertyId)
             ?? this.contactDateProps.find(p => p.id === propertyId);
    if (found) return found.inputType;
    found = this.companyStaticProps.find(p => p.id === propertyId)
         ?? this.companyDateProps.find(p => p.id === propertyId);
    if (found) return found.inputType;
    const customFound = this.customAttributes.find(a => String(a.id) === String(propertyId));
    if (customFound) return `custom_${customFound.type}`;
    return 'text';
  }

  private operatorCategory(property: string): 'text' | 'select' | 'number' | 'date' {
    const it = this.getPropertyInputType(property);
    if (it === 'date') return 'date';
    if (it.startsWith('select_') || it === 'custom_boolean' || it === 'custom_list') return 'select';
    if (it === 'custom_number') return 'number';
    return 'text';
  }

  private categoryOperators(property: string): { value: string; label: string }[] {
    return this.operatorsByCategory[this.operatorCategory(property)] ?? this.operatorsByCategory['text'];
  }

  private operatorLabel(value: string): string {
    for (const cat of Object.values(this.operatorsByCategory)) {
      const found = cat.find(o => o.value === value);
      if (found) return found.label;
    }
    return value;
  }

  private syncToFormArray(): void {
    this.rules.clear({ emitEvent: false });
    this.conditions.forEach((cond, index) => {
      this.rules.push(this.fb.group({
        segmentId: [0],
        groupIndex: [index + 1, Validators.required],
        groupOperator: [cond.groupOperator],
        property: [cond.property, Validators.required],
        operator: [cond.operator, Validators.required],
        valueExpression: [cond.valueExpression, this.operatorNeedsValue(cond.operator) ? Validators.required : []],
        executionOrder: [index + 1, Validators.required]
      }), { emitEvent: false });
    });
    this.rules.updateValueAndValidity();
  }

  private rebuildUIFromFormArray(): void {
    this.conditions = [];
    const flatRules = (this.rules.value as FilterRuleRequest[])
      .slice()
      .sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0));
    flatRules.forEach(rule => {
      this.conditions.push({
        property: rule.property,
        operator: rule.operator,
        valueExpression: rule.valueExpression ?? '',
        groupOperator: (rule.groupOperator as 'AND' | 'OR') || 'AND'
      });
    });
  }
}
